import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import './SlidableMenu.css';
import { AnimatedMenuController, AnimatedMenuViewGeneric, IAnimatedMenuController, IAnimatedMenuItemProps, IAnimatedMenuProps } from "../../pages/desktopPage/AnimatedMenu";

interface ISlidableMenuProps{
    items: Array<React.ReactNode>;
    selectedItemIndex?: number;
    onSelect?: (index: number)=>void;
}

const AnimatedMenuItem = ({itemContent, onClick}: IAnimatedMenuItemProps)=>{
    return <div className="commonSlidableMenu_item AnimatedMenuItem">
        <div className={`button commonSlidableMenu_button ${/*i == selectedItemInternal ? "commonSlidableMenu_button--active" : ''*/''}`} 
            onClick={onClick}
        >
            {itemContent}
        </div>
    </div>
    /*return <div className="button AnimatedMenuItem" onClick={onClick}>
        {itemContent}
    </div>*/
}

const AnimatedMenuView = ({controller}: {controller: IAnimatedMenuController})=>{
    return <AnimatedMenuViewGeneric 
        controller={controller} 
        ItemView={AnimatedMenuItem}>
    </AnimatedMenuViewGeneric>
}

export const AnimatedMenuCustom = ({items, selectedIndex, onSelect, itemsRef}: IAnimatedMenuProps)=>{
    return <AnimatedMenuController
        items={items}
        selectedIndex={selectedIndex}
        onSelect={onSelect}
        itemsRef={itemsRef}
        View={AnimatedMenuView}
        childrenSelector={(parent)=>{
            return [...parent.children].map((it)=>it.children[0] as HTMLElement);
        }}
    ></AnimatedMenuController>
}
export const SlidableMenu = ({selectedItemIndex, onSelect, items}: ISlidableMenuProps)=>{
    const [position, setPosition] = useState(0);
    const [isButtonAnimation, setButtonAnimation] = useState(false);
    const itemsRef = useRef<HTMLDivElement>(null);
    const itemsListRef = useRef<HTMLDivElement>(null);
    const [scrollFix, setScrollFix] = useState(0);
    const [lastDelta, setLastDelta] = useState(0);
    const [inited, setInited] = useState(false);
    const [noScroll, setNoScroll] = useState(false);

    const [arrowsMode, setArrowsMode] = useState<'left' | 'center' | 'right'>('center');
    const slidableWheelRef = useRef<HTMLDivElement>(null);



    useEffect(()=>{
        if (!slidableWheelRef.current){
            return;
        }
        const el = slidableWheelRef.current;
        const handleWheel = (e: WheelEvent)=>{
            e.preventDefault();
            setScrollFix(last=>last+e.deltaY - e.deltaX); 
            setLastDelta((last)=>last+=e.deltaY - e.deltaX);
            const maxPercent = -100 + (itemsRef.current?.parentElement.clientWidth / itemsRef.current?.clientWidth)*100;
            //const speedScaler = 50 * (itemsRef.current?.parentElement.clientWidth / 10);
            const speedScaler = 10;
            setPosition(last=>Math.max(Math.min(last+(e.deltaY - e.deltaX) / speedScaler, 0), maxPercent))
        }
        el.addEventListener('wheel', handleWheel, {passive: false});
        return ()=>{
            el.removeEventListener('wheel', handleWheel);
        }
    }, [slidableWheelRef.current]);

    useEffect(()=>{
        setInited(true);
    }, []);

    const [selectedItemInternal, setSelectedItemInternal] = useState(selectedItemIndex);
    useEffect(()=>{
        if (selectedItemIndex != undefined && selectedItemIndex!=selectedItemInternal){
            setSelectedItemInternal(selectedItemIndex);
            onSelect?.(selectedItemIndex);
        }
    }, [selectedItemIndex]);

    //const items = useMemo(()=> new Array(100).fill(0).map((it, i)=>'23563453424523'.slice(0, Math.floor(Math.random() * 10 + 1))), []);

    const sizes = useMemo(()=>{
        console.log('sizes calc');
        const itemsContainer = itemsListRef.current || itemsRef.current;
        if (itemsContainer){
            return [...itemsContainer.children].map(it=>it.getBoundingClientRect().width);
        }
    }, [itemsRef.current, itemsListRef.current, inited, itemsRef.current?.parentElement.clientWidth, itemsRef.current?.clientWidth]);

    const [resizeFix, setResizeFix] = useState(window.innerWidth);

    useEffect(()=>{
        if (!itemsRef.current){
            return;
        }
        if (itemsRef.current?.parentElement.clientWidth >= itemsRef.current.clientWidth){
            setNoScroll(true);
        } else {
            setNoScroll(false);
        }
    }, [resizeFix, itemsRef.current, itemsRef.current?.parentElement.clientWidth, itemsRef.current?.clientWidth])

    useEffect(()=>{
        const handler = ()=>{
            setResizeFix(window.innerWidth);
        };
        window.addEventListener('resize', handler);
        return ()=> window.removeEventListener('resize', handler);
    }, []);

    const limitScroll = (value:number)=>{
        return Math.max(Math.min(value, 0), -100 + (itemsRef.current?.parentElement.clientWidth / itemsRef.current?.clientWidth)*100)
    }

    useEffect(()=>{
        if (!sizes){
            return;
        }

        if (isButtonAnimation){
            setButtonAnimation(false);
            return;
        }

        if (lastDelta ==0){
            return;
        }

        const tid = setTimeout(()=>{
            let sizeCounter = 0;
            const sizeIndex = sizes.findIndex(it=>{
                if (sizeCounter + it *0.5 - (-position * itemsRef.current.clientWidth / 100) > 0){
                    return true;
                }
                sizeCounter += it;
            });
            let sizeCounterRight = 0;
            const sizeIndexRight = sizes.findIndex(it=>{
                if (sizeCounterRight + it *0.5 - (-position * itemsRef.current.clientWidth / 100 + itemsRef.current?.parentElement.clientWidth) > 0){
                    return true;
                }
                sizeCounterRight += it;
            });
            //console.log(sizeIndexRight, sizeCounterRight, itemsRef.current.clientWidth);
            //console.log(sizeIndex, sizeCounter, itemsRef.current.clientWidth);
            if (sizeIndex == 0 || (lastDelta)>0){
                setPosition(limitScroll(-sizeCounter / itemsRef.current.clientWidth * 100)); 
            } else {
                setPosition(limitScroll((-sizeCounterRight + itemsRef.current?.parentElement.clientWidth) / itemsRef.current.clientWidth * 100));
            }
            setLastDelta(0);
        }, 300);
        return ()=>{
            clearTimeout(tid);
        }
    }, [sizes, position, scrollFix, resizeFix, isButtonAnimation, itemsRef.current?.clientWidth, itemsRef.current?.parentElement.clientWidth]);

    useEffect(()=>{
        setPosition(limitScroll(position));
    }, [resizeFix]);

    const handleLeft = useCallback(()=>{
        let sizeCounter = 0;
        const sizeIndex = sizes.findIndex((it, i)=>{
            if (sizeCounter + it * 0.5 + (sizes[i - 1] || it) > (-position * itemsRef.current.clientWidth / 100)){
                //sizeCounter += it;
                return true;
            }
            sizeCounter += it;
        });
        //console.log(sizeCounter, sizeIndex);
        setLastDelta(1);
        setButtonAnimation(true);
        setPosition(last=>limitScroll(-sizeCounter / itemsRef.current.clientWidth * 100));
    }, [sizes, itemsRef.current, position]);

    const handleRight = useCallback(()=>{
        let sizeCounterRight = 0;
        const sizeIndexRight = sizes.findIndex(it=>{
            if (sizeCounterRight + it*0.5 > (-position * itemsRef.current.clientWidth / 100 + itemsRef.current?.parentElement.clientWidth)){
                sizeCounterRight += it;
                return true;
            }
            sizeCounterRight += it;
        });
        setLastDelta(-1);
        setButtonAnimation(true);
        setPosition(limitScroll((-sizeCounterRight + itemsRef.current?.parentElement.clientWidth) / itemsRef.current.clientWidth * 100));
    }, [sizes, itemsRef.current, position]);

    const animatedEnabled = false;

    return <div className="commonSlidableMenu_wrapper">
        <div ref={slidableWheelRef} className={`commonSlidableMenu ${noScroll ? 'commonSlidableMenu--noScroll' : ''}`}>
        <div className="commonSlidableMenu_arrows">
            { (!noScroll) && (arrowsMode == 'center' || arrowsMode == 'right') && <div className="button commonSlidableMenu_arrow commonSlidableMenu_arrow_left" onClick={handleLeft}>
                left
            </div>}
            { (!noScroll) && (arrowsMode == 'right') &&<div className="button commonSlidableMenu_arrow commonSlidableMenu_arrow_right" onClick={handleRight}>
                right
            </div>}
        </div>
        <div className="commonSlidableMenu_scroll">
            <div ref={itemsRef} 
                className="commonSlidableMenu_items" 
                style={{
                    left: !noScroll ? `${0*-position}%` : '',
                    transform: !noScroll ? `translateX(${position}%)` : ''
                }}
            >
                {!animatedEnabled ? 
                <AnimatedMenuCustom selectedIndex={selectedItemInternal} items={items} onSelect={(index)=>{
                    setSelectedItemInternal(index);
                    onSelect?.(index);
                }} itemsRef={itemsListRef}></AnimatedMenuCustom>
                :
                items.map((it, i)=><div className="commonSlidableMenu_item">
                    <div className={`button commonSlidableMenu_button ${i == selectedItemInternal ? "commonSlidableMenu_button--active" : ''}`} 
                        onClick={()=>{
                            setSelectedItemInternal(i);
                            onSelect?.(i);
                        }}
                    >
                        {it}
                    </div>
                </div>)
                }
            </div>
        </div>
        <div className="commonSlidableMenu_arrows">
            { (!noScroll) && (arrowsMode == 'right') && <div className="button commonSlidableMenu_arrow commonSlidableMenu_arrow_left" onClick={handleLeft}>
                left
            </div>}
            { (!noScroll) && (arrowsMode == 'right' || arrowsMode == 'center') &&<div className="button commonSlidableMenu_arrow commonSlidableMenu_arrow_right" onClick={handleRight}>
                right
            </div>}
        </div>
    </div>
    </div>
}